import * as topojson from 'topojson-client';
import type { Topology } from 'topojson-specification';
import type { FeatureCollection } from 'geojson';
import { MetaSchema, ComuneLightArraySchema, ComuneFullArraySchema } from './schemas';
import type { Meta, ComuneLight, ComuneFull, TopoLevel, DistrictMeta } from './types';
import { cacheGet, cachePut, cacheSweep } from './cache';
import { activeMapCountry, activeMapCountryConfig } from './active-country';
import { countryDataPath } from '@/features/map-core/countries';

// Override через VITE_DATA_BASE_URL (например, https://data.lodestar.place на R2 в Phase 6).
// Без переменной — same-origin под /data/ (web/public/data/ symlink в dev, копия в CI).
export const DATA_BASE_URL: string =
  (import.meta.env.VITE_DATA_BASE_URL as string | undefined) ?? '/data';
const BASE = DATA_BASE_URL;

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed: ${url} — ${res.status}`);
  return res.json();
}

/** URL файла данных активной страны. */
function dataUrl(file: string): string {
  return `${BASE}/${countryDataPath(activeMapCountryConfig(), file)}`;
}

/**
 * Ключ IDB для активной страны. Префикс обязателен: без него вторая страна
 * перезаписывает кеш первой, потому что имена ключей совпадают.
 */
function ckey(kind: string, version: string): string {
  return `${activeMapCountry()}_${kind}_${version}`;
}

// ─── Meta ────────────────────────────────────────────────────────────────────

/**
 * Ключ версии — тоже на страну. Раньше он был один глобальный, и sweep при смене
 * версии сносил кеш ВСЕХ стран: страна с более свежей версией стирала данные
 * остальных независимо от префиксов ключей.
 */
function versionKey(): string {
  return `__meta_version_${activeMapCountry()}`;
}

export async function loadMeta(): Promise<Meta> {
  const raw = await fetchJson<unknown>(dataUrl('meta.json'));
  const meta = MetaSchema.parse(raw);

  const cc = activeMapCountry();
  const vkey = versionKey();

  // Persist last-seen version в самой IDB → переживает page reload.
  // На несовпадение — sweep ключей ЭТОЙ страны, не относящихся к текущей версии.
  // Ключи других стран и их версии не трогаем.
  const previous = await cacheGet<string>(vkey);
  if (previous && previous !== meta.version) {
    await cacheSweep(
      (key) => !key.startsWith(`${cc}_`) || key.endsWith(`_${meta.version}`),
    );
  }
  if (previous !== meta.version) {
    await cachePut(vkey, meta.version);
  }

  return meta;
}

// ─── Light ───────────────────────────────────────────────────────────────────

export async function loadLight(version: string): Promise<ComuneLight[]> {
  const cacheKey = ckey('light', version);
  const cached = await cacheGet<ComuneLight[]>(cacheKey);
  if (cached) return cached;

  const raw = await fetchJson<unknown>(dataUrl(activeMapCountryConfig().lightFile));
  const data = ComuneLightArraySchema.parse(raw);
  await cachePut(cacheKey, data);
  return data;
}

// ─── Full ────────────────────────────────────────────────────────────────────

export async function loadFull(version: string): Promise<ComuneFull[]> {
  const cacheKey = ckey('full', version);
  const cached = await cacheGet<ComuneFull[]>(cacheKey);
  if (cached) return cached;

  const raw = await fetchJson<unknown>(dataUrl(activeMapCountryConfig().fullFile));
  const data = ComuneFullArraySchema.parse(raw);
  await cachePut(cacheKey, data);
  return data;
}

// ─── TopoJSON ────────────────────────────────────────────────────────────────

export async function loadTopo(
  level: TopoLevel,
  version: string,
): Promise<FeatureCollection> {
  const cacheKey = ckey(`topo_${level}`, version);
  const cached = await cacheGet<FeatureCollection>(cacheKey);
  if (cached) return cached;

  const cfg = activeMapCountryConfig();
  const levelCfg = cfg.levels.find((l) => l.key === level);
  if (!levelCfg) throw new Error(`Level '${level}' is not configured for country '${cfg.cc}'`);

  const topo = await fetchJson<Topology>(dataUrl(levelCfg.topoFile));
  // Имя объекта внутри TopoJSON совпадает с ключом уровня — это контракт,
  // который обеспечивает шаг 02_boundaries.py.
  const geojson = topojson.feature(topo, topo.objects[level]) as FeatureCollection;
  await cachePut(cacheKey, geojson);
  return geojson;
}

// ─── Districts meta ────────────────────────────────────────────────────────────
// Лёгкий справочник имён промежуточного уровня (у Италии — 514 районов SLL/CC),
// keyed by code. Страна без такого уровня возвращает пустую карту.

/** Как страна публикует справочник районов: Италия — слева, Испания — справа. */
interface RawDistrictMeta {
  dist_code: string;
  kind?: 'sll' | 'cc';
  name_it?: string;
  name_de?: string | null;
  reg?: string;
  n_comuni?: number;
  name?: string;
  cpro?: string;
  prov_name?: string;
  n_units?: number;
}

function adaptDistrict(d: RawDistrictMeta): DistrictMeta {
  return {
    dist_code: d.dist_code,
    name: d.name_it ?? d.name ?? d.dist_code,
    // Двуязычное имя есть только у comunità comprensoriali Южного Тироля.
    name_alt: d.kind === 'cc' && d.name_de && d.name_de !== d.name_it ? d.name_de : null,
    parent: d.reg ?? d.prov_name ?? d.cpro ?? '',
    n_units: d.n_comuni ?? d.n_units ?? 0,
    kind: d.kind ?? '',
  };
}

export async function loadDistrictsMeta(version: string): Promise<Map<string, DistrictMeta>> {
  const file = activeMapCountryConfig().districtsMetaFile;
  if (!file) return new Map();

  const cacheKey = ckey('districts_meta', version);
  const cached = await cacheGet<RawDistrictMeta[]>(cacheKey);
  const arr = cached ?? (await fetchJson<RawDistrictMeta[]>(dataUrl(file)));
  if (!cached) await cachePut(cacheKey, arr);
  return new Map(arr.map((d) => [d.dist_code, adaptDistrict(d)]));
}
